import { ApolloProvider } from '@apollo/client';
import { CloseButton } from 'components/ErrorToast';
import Loader, { GlobalLoader } from 'components/Loader';
import React, { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import routes from 'routes';
import { client } from 'services/apollo/client';
import 'services/i18n';
import { ThemeProvider } from 'styled-components';
import { GlobalStyle, theme } from 'theme';
import authCognito from '../../services/cognito/lib';
import { Amplify } from 'aws-amplify';
import awsConfig from '../../services/cognito/AwsConfig';
// Amplify Auth configuration
// @ts-ignore
Amplify.configure(awsConfig);

// Defer loading for pages
const HomePage = lazy(() => import('pages/Home'));
const TableGroupPage = lazy(() => import('pages/TableGroup'));
const OrderDetailPage = lazy(() => import('pages/OrderDetails'));
const OrderGroupNotePage = lazy(() => import('pages/OrderGroupNote'));
const OrderGroupDetailsPage = lazy(() => import('pages/OrderGroupDetails'));
const TableOrderPage = lazy(() => import('pages/TableOrder'));
const TableGroupHistoryPage = lazy(() => import('pages/TableGroupHistory'));
const TableHistoryPage = lazy(() => import('pages/TableHistory'));
const OrderEntryPage = lazy(() => import('pages/EntryOrder'));
const OrderSummaryPage = lazy(() => import('pages/OrderSummary'));
const LoginPage = lazy(() => import('pages/Login'));
const CallBackPage = lazy(() => import('pages/CallBack'));
const SearchGuestPage = lazy(() => import('pages/SearchGuest'));
const PasswordExpired = lazy(() => import('pages/PasswordExpired'));
const LogoutPage = lazy(() => import('pages/Logout'));

toast.configure({
  hideProgressBar: true,
  toastClassName: 'app-toast',
  className: 'app-toast-container',
  closeButton: <CloseButton />,
});

export const AppContent = () => {
  const location = useLocation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);

  // Define public paths
  const publicPaths = ['/login', '/callback', '/logout'];
  const isPublicPath = (path: string) =>
    publicPaths.some((p) => path.startsWith(p));

  const checkSession = async () => {
    try {
      const isAuthenticated = await authCognito.isAuthenticated();
      setIsAuthenticated(isAuthenticated);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  // If still loading, show loader
  if (loading) {
    return <GlobalLoader />;
  }

  // If user is not authenticated and trying to access a protected route
  if (!isAuthenticated && !isPublicPath(location.pathname)) {
    return (
      <Switch>
        <Redirect
          to={{
            pathname: '/login',
            state: { from: location },
          }}
        />
      </Switch>
    );
  }

  return (
    <Switch>
      {/* Public routes */}

      <Route path={routes.login()} component={LoginPage} />
      <Route path={routes.callback()} component={CallBackPage} />
      <Route path={routes.logout()} component={LogoutPage} />

      {/* Protected route - for authenticated users only */}
      <Route exact path={routes.index()} component={HomePage} />
      <Route exact path={routes.tableGroup()} component={TableGroupPage} />
      <Route exact path={routes.searchGuests()} component={SearchGuestPage} />
      <Route
        exact
        path={routes.orderGroupNote()}
        component={OrderGroupNotePage}
      />
      <Route exact path={routes.orderDetails()} component={OrderDetailPage} />
      <Route
        exact
        path={routes.orderGroupDetails()}
        component={OrderGroupDetailsPage}
      />
      <Route
        exact
        path={routes.tableGroupHistory()}
        component={TableGroupHistoryPage}
      />
      <Route exact path={routes.tableHistory()} component={TableHistoryPage} />
      <Route exact path={routes.entryOrder()} component={OrderEntryPage} />
      <Route exact path={routes.orderSummary()} component={OrderSummaryPage} />
      <Route path={routes.tableOrder()} component={TableOrderPage} />

      {/* Default route */}
      <Route path="*">
        <Redirect to="/" />
      </Route>
    </Switch>
  );
};

const App = () => (
  <ThemeProvider theme={theme}>
    <ApolloProvider client={client}>
      <GlobalStyle />
      <Suspense fallback={<Loader />}>
        <Router>
          <AppContent />
        </Router>
      </Suspense>
    </ApolloProvider>
  </ThemeProvider>
);
export default App;
